import React, { useContext, useCallback, useEffect, useState, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import produce from 'immer';
import { Issue, IssueConditionValue, MonitorSeverity } from '@moderepo/mode-apis';
import { capitalizeAll, ITEMS_PER_PAGE_10, ITEMS_PER_PAGE_LIST, GenericMessageBox, PaginationBar, PAGING_MIN_PAGES, SelectInputOption,
    useSearchParams } from '@moderepo/mode-app-components';
import { fetchProjectAgents, ritzDataContext, selectProjectAgent, selectProjectAgents, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchIssues, rcDataContext, selectIssues, UIFetchIssueFilters } from '@moderepo/mode-data-state-robot-cloud';
import { modeUIContext, selectSlideOutPanelOptions, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useNavigate } from 'react-router-dom';
import { useRitzTranslation } from '../../utils/customHooks';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import * as RCUtils from './RCUtils';
import { RCIssuesTableDataItem, RCIssuesTable, RCIssueTableFilters, RCIssuesTableProps } from '../../components/robot-cloud/RCIssuesTable';
import { RCSlideOutPanelCompType, RCIssuePreviewCompOptions, isIssuePreviewCompOptions } from './RCProjectConfig';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { RCRouteHelper } from './RCRouteHelper';



/**
 * Create an Array of IssueSeverity option used for filter by issue severity dropdown
 * @param trans
 */
const createFilterByIssueSeverityOptions = (trans: TFunction): readonly SelectInputOption<MonitorSeverity | ''>[] => {
    return [{
        label: capitalizeAll(trans('label.show_all')),
        value: '',
    }, ...(Object.values(MonitorSeverity).filter((v: any): boolean => {
        return typeof v === 'number';
    }) as MonitorSeverity[]).map((severity: MonitorSeverity): SelectInputOption<MonitorSeverity> => {
        return {
            label: capitalizeAll(trans(RCUtils.getMonitorSeverityName(severity))),
            value: severity,
        };
    })];
};


enum ResolveStatus {
    RESOLVED = 1,
    NOT_RESOLVED = 2,
}


const convertBooleanToResolveStatus = (value: boolean | undefined): ResolveStatus | undefined => {
    if (value !== undefined) {
        if (value === true) {
            return ResolveStatus.RESOLVED;
        }
        return ResolveStatus.NOT_RESOLVED;
    }
    return undefined;
};


/**
 * Create an Array of resolve status option used for filter by resolve status dropdown
 * @param trans
 */
const createResolveStatusFilterOptions = (trans: TFunction): readonly SelectInputOption<ResolveStatus | ''>[] => {
    return [{
        label: capitalizeAll(trans('label.show_all')),
        value: '',
    }, {
        label: capitalizeAll(trans('label.resolved')),
        value: ResolveStatus.RESOLVED,
    }, {
        label: capitalizeAll(trans('label.not_resolved')),
        value: ResolveStatus.NOT_RESOLVED,
    }];
};



/**
 * The set of fields to be hidden from the table. By default, we will show all columns. If we need to hide some column,
 * set column to 'true'
 */
export interface IssueTableHiddenFields {
    readonly resolved?: boolean;
    readonly id?: boolean;
    readonly deviceId?: boolean;
    readonly robotId?: boolean;
    readonly homeId?: boolean;
    readonly alertId?: boolean;
    readonly values?: boolean;
    readonly severity?: boolean;
    readonly description?: boolean;
    readonly ticketLink?: boolean;
    readonly createdBy?: boolean;
    readonly createdAgentId?: boolean;
    readonly createdAt?: boolean;
    readonly resolvedAgentId?: boolean;
    readonly resolvedAt?: boolean;
}



interface RCIssuesContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly userId?: number;
    readonly homeId?: number;
    readonly deviceId?: number;
    readonly title?: string;
    readonly itemsPerPage?: number;
    readonly hiddenFields?: IssueTableHiddenFields;
    readonly readonly?: boolean;
    readonly filters?: UIFetchIssueFilters;
    readonly showFilterOptions?: boolean;
}


interface IssueSearchFields {
    readonly severity: MonitorSeverity | undefined
    readonly resolveStatus: number | undefined
}



/**
 * This container display ALL the Project's issues. We created a container for this instead of placing this code in the IssuePage so that
 * this can be reused and shown in other pages.
 */
export const RCIssuesContainer = (props: RCIssuesContainerProps) => {
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const slideOutPanelOptions = selectSlideOutPanelOptions(uiState);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const projectAgents = selectProjectAgents(dataState, props.projectId);
    const [selectedIssueId, setSelectedIssueId] = useState<number | undefined>();
    const navigate = useNavigate();

    // Filtered by IssueSeverity options
    const issueSeverityFilterOptions = useMemo(() => {
        return createFilterByIssueSeverityOptions(trans);
    }, [trans]);


    // Filtered by resolve status options
    const resolveStatusFilterOptions = useMemo(() => {
        return createResolveStatusFilterOptions(trans);
    }, [trans]);



    const {
        searchParams, onItemsPerPageChange, onOtherFieldsChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<IssueSearchFields>({
        searchText  : '',
        itemsPerPage: props.itemsPerPage || ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : {
            severity     : undefined,
            resolveStatus: convertBooleanToResolveStatus(props.filters?.resolved),
        },
    });



    // Select the list of issues based on the current filters
    const issues = selectIssues(rcDataState, props.projectId, {
        ...props.filters,
        pageNumber: searchParams.currentPage,
        pageSize  : searchParams.itemsPerPage,
        severity  : searchParams.otherFields.severity,
        resolved  : searchParams.otherFields.resolveStatus !== undefined
            ? searchParams.otherFields.resolveStatus === ResolveStatus.RESOLVED : undefined,
    });


    // Handlers for issue severity filter changed event
    const onFilterByIssueSeverityChange = useCallback((severity: MonitorSeverity | '') => {
        if (severity) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: any) => {
                draft.severity = severity;
            }));
        } else {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: any) => {
                draft.severity = undefined;
            }));
        }
    }, [onOtherFieldsChange, searchParams.otherFields]);


    // Handlers for resolve status filter changed event
    const onFilterByResolveStatusChange = useCallback((status: ResolveStatus | '') => {
        if (status !== '') {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: any) => {
                draft.resolveStatus = status;
            }));
        } else {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: any) => {
                draft.resolveStatus = undefined;
            }));
        }
    }, [onOtherFieldsChange, searchParams.otherFields]);



    // Create the filter settings to be passed to the IssueTableComp
    const issueFilterSettings: RCIssueTableFilters = useMemo(() => {
        return {
            severity: {
                currentValue: searchParams.otherFields.severity,
                label       : capitalizeAll(trans('label.issue_filter.severity')),
                options     : issueSeverityFilterOptions,
                onChange    : onFilterByIssueSeverityChange,
            },
            resolveStatus: {
                currentValue: searchParams.otherFields.resolveStatus,
                label       : capitalizeAll(trans('label.issue_filter.resolve_status')),
                options     : resolveStatusFilterOptions,
                onChange    : onFilterByResolveStatusChange,
            },
        };
    }, [trans, searchParams.otherFields, issueSeverityFilterOptions, onFilterByIssueSeverityChange,
        resolveStatusFilterOptions, onFilterByResolveStatusChange]);



    /**
     * Fetch issues base on the current pagination params
     */
    const fetchMoreIssues = useCallback(async () => {
        if (project) {
            rcDataDispatch(fetchIssues(
                project.id, {
                    ...props.filters,
                    pageNumber: searchParams.currentPage,
                    pageSize  : searchParams.itemsPerPage,
                    severity  : searchParams.otherFields.severity,
                    resolved  : searchParams.otherFields.resolveStatus !== undefined
                        ? searchParams.otherFields.resolveStatus === ResolveStatus.RESOLVED : undefined,
                },
            )).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [project, rcDataDispatch, props.filters, searchParams.currentPage, searchParams.itemsPerPage, searchParams.otherFields.severity,
        searchParams.otherFields.resolveStatus]);



    /**
     * This use effect is used for fetching more issue when the itemsPerPage or currentPage from the table pagination changed.
     */
    useEffect(() => {
        fetchMoreIssues();
    }, [dataDispatch, fetchMoreIssues]);



    /**
     * This used effect is used for loading issues on component loads or when issues are cleared
     */
    useEffect(() => {
        if (issues === undefined) {
            fetchMoreIssues();
        }
    }, [fetchMoreIssues, issues]);


    /**
     * This use effect is used for loading all project agents data. We will need this data to show the agent's name/email.
     */
    useEffect(() => {
        if (!projectAgents) {
            dataDispatch(fetchProjectAgents(props.projectId));
        }
    }, [projectAgents, dataDispatch, props.projectId]);


    /**
     * Localize the Issues data before passing it to the IssuesTableComp for display
     */
    const localizedIssuesData = useMemo((): readonly RCIssuesTableDataItem[] | undefined => {
        const issueList: readonly Issue[] | undefined = issues?.items;

        if (issueList && issueList.length > 0) {
            return issueList.map((issue: Issue): RCIssuesTableDataItem => {
                const createdAgent = issue.createdAgentId && projectAgents
                    ? selectProjectAgent(dataState, props.projectId, issue.createdAgentId)
                    : undefined;

                const resolvedAgent = issue.resolvedAgentId
                    ? selectProjectAgent(dataState, props.projectId, issue.resolvedAgentId)
                    : undefined;

                return {
                    actualValue : issue,
                    displayValue: {
                        status     : capitalizeAll(trans(issue.resolvedAt ? 'label.resolved' : 'label.not_resolved')),
                        id         : issue.id.toString(),
                        monitorId  : issue.alertConditionId ? issue.alertConditionId.toString() : capitalizeAll(trans('label.none')),
                        deviceId   : issue.deviceId?.toString() || '',
                        robotId    : issue?.robotId || '',
                        homeId     : issue.homeId.toString(),
                        description: issue.description,
                        severity   : capitalizeAll(trans(RCUtils.getMonitorSeverityName(issue.severity))),
                        values     : issue.conditionValues ? issue.conditionValues.map((value: IssueConditionValue) => {
                            return `${value.valueName}:${value.value !== undefined ? value.value : trans('label.unknown').toUpperCase()}`;
                        }).join(', ') : capitalizeAll(trans('label.none')),
                        ticketLink: issue.ticketLink || '',
                        createdBy : issue.createdAgentId
                            ? createdAgent?.name || issue.createdAgentId.toString()     // if issue is created by an agent, use agent's name or id
                            : issue.alertConditionId?.toString() || '',                 // else issue must be created by Alert condition
                        createdAgentId : issue.createdAgentId ? issue.createdAgentId.toString() : '',
                        resolvedAgentId: issue.resolvedAt
                            ? String(resolvedAgent?.name || issue.resolvedAgentId || capitalizeAll(trans('system.name')))
                            : capitalizeAll(trans('label.not_resolved')),
                        createdAt : getDateTimeAndElapsedTime(issue.createdAt, trans, false),
                        resolvedAt: issue.resolvedAt ? getDateTimeAndElapsedTime(issue.resolvedAt, trans, false) : '',
                    },
                    selected: selectedIssueId === issue.id,
                };
            });
        }

        return undefined;
    }, [trans, issues, selectedIssueId, projectAgents, dataState, props.projectId]);


    // The table settings that we need to pass to the IssueTableComp. We will create and cache it instead of creating it each time we
    // re-render this container.
    const issuesTableSettings = useMemo((): RCIssuesTableProps['fieldsSettings'] => {
        return {
            fields: {
                preview: {
                    label: '',      // The column doesn't need a label so we can leave it blank
                },
                id: {
                    dataItemProp: 'id',
                    label       : capitalizeAll(trans('label.id')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.id,
                },
                deviceId: {
                    dataItemProp: 'deviceId',
                    label       : capitalizeAll(trans('label.device_id')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.deviceId,
                },
                robotId: {
                    dataItemProp: 'robotId',
                    label       : capitalizeAll(trans('label.robot_id')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.robotId,
                },
                homeId: {
                    dataItemProp: 'homeId',
                    label       : capitalizeAll(trans('label.home_id')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.homeId,
                },
                monitorId: {
                    dataItemProp: 'monitorId',
                    label       : capitalizeAll(trans('label.monitor_id')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.alertId,
                },
                values: {
                    dataItemProp: 'values',
                    label       : capitalizeAll(trans('label.issue_triggered_value', { count: 2 })),
                    sortable    : false,
                    hidden      : props.hiddenFields?.values,
                },
                description: {
                    dataItemProp: 'description',
                    label       : capitalizeAll(trans('label.description')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.description,
                },
                severity: {
                    dataItemProp: 'severity',
                    label       : capitalizeAll(trans('label.severity')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.severity,
                },
                ticketLink: {
                    dataItemProp: 'ticketLink',
                    label       : capitalizeAll(trans('label.external_ticket_link')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.ticketLink,
                },
                createdBy: {
                    dataItemProp: 'createdBy',
                    label       : capitalizeAll(trans('label.created_by')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.createdBy,
                },
                createdAgentId: {
                    dataItemProp: 'createdAgentId',
                    label       : capitalizeAll(trans('label.created_by')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.createdAgentId,
                },
                resolvedAgentId: {
                    dataItemProp: 'resolveAgentId',
                    label       : capitalizeAll(trans('label.resolved_by')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.resolvedAgentId,
                },
                createdAt: {
                    dataItemProp: 'createdAt',
                    label       : capitalizeAll(trans('label.date')),
                    hidden      : props.hiddenFields?.createdAt,
                },
                resolvedAt: {
                    dataItemProp: 'resolvedAt',
                    label       : capitalizeAll(trans('label.date_resolved')),
                    hidden      : props.hiddenFields?.resolvedAt,
                },
                status: {
                    dataItemProp: 'resolved',
                    label       : capitalizeAll(trans('label.status')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.resolved,
                },
            },
        };
    }, [trans, props.hiddenFields]);



    // The table pagination settings that we need to pass to the IssueTableComp. We will create and cache it instead of creating it each time we
    // re-render this container.
    const issuesTablePaginationBar = useMemo(() => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                totalItems      : issues && issues.range ? issues.range.total : undefined,
                hasMoreItems    : (issues && issues.range
                    ? issues.range.total > searchParams.itemsPerPage * (searchParams.currentPage + 1)
                    : (!issues || issues.items.length >= searchParams.itemsPerPage)),
                texts: {
                    itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                        item_name: trans('label.item', { count: 2 }),
                    })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [trans, issues, searchParams, onItemsPerPageChange, onNextPage, onPrevPage, onPage]);



    // This useEffect is used for checking the slideOutPanelOptions from the UI state to see if there is any Issue Preview slide out
    // component being selected. If there is, we will automatically select the issue in this local component as well
    useEffect(() => {
        if (isIssuePreviewCompOptions(slideOutPanelOptions)) {
            setSelectedIssueId(slideOutPanelOptions.compProps.data.issueId);
        } else {
            setSelectedIssueId(undefined);
        }
    }, [slideOutPanelOptions]);



    /**
     * On issue selected, go immediately to the issue details page.
     */
    const onIssueSelected = useCallback((issue: Issue) => {
        RCRouteHelper.getInstance(navigate).gotoInvestigateIssuePage({
            projectId: props.projectId,
            homeId   : issue.homeId,
            deviceId : issue.deviceId,
            issueId  : issue.id,
        });
    }, [navigate, props.projectId]);


    /**
     * Change the selected issue to preview to be the specified issueId
     * If 'issue' object is undefined, it could be either to unselect the selected Issue or to create new Issue. If this function is called
     * to create a new Issue, the 'createNew' flag need to be true
     */
    const onPreviewIssue = useCallback((issueId: number | undefined) => {
        setSelectedIssueId(issueId);

        const options: RCIssuePreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : RCSlideOutPanelCompType.ISSUE_PREVIEW,
            compProps: {
                data: {
                    projectId: props.projectId,
                    issueId,
                },
                context: { userId: props.userId, homeId: props.homeId, deviceId: props.deviceId },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.deviceId, props.homeId, props.projectId, props.userId, uiDispatch]);



    /**
     * On Create issue button clicked, set selectedIssue to be undefined and pass 'true' for createIssue
     */
    const onCreateIssueClicked = useCallback(async () => {
        onPreviewIssue(undefined);
    }, [onPreviewIssue]);



    return (
        <>
            {project && (
                <RCIssuesTable
                    className={clsx(props.className)}
                    listData={localizedIssuesData}
                    title={props.title || capitalizeAll(trans('label.issue', { count: 2 }))}
                    icon="report"
                    onListItemSelected={onIssueSelected}
                    onPreviewItemClicked={(issue: Issue) => {
                        onPreviewIssue(issue.id);
                    }}
                    filters={props.showFilterOptions ? issueFilterSettings : undefined}
                    customPrimaryActions={!props.readonly ? [{
                        label  : capitalizeAll(trans('label.add')),
                        icon   : 'add',
                        variant: 'contained',
                        color  : 'primary',
                        onClick: onCreateIssueClicked,
                    }] : undefined}
                    paginationComp={issuesTablePaginationBar}
                    fieldsSettings={issuesTableSettings}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.issue', { count: 2 })),
                            })}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.issue', { count: 2 })),
                            })}
                        />
                    )}
                />
            )}


            {!project && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('RCIssuesContainer', RCIssuesContainer);
